import log from "logbeacon";
import { authCacheManager } from "@/src/features/auth/compat/auth-cache-compat";
import { LoginType } from "@/src/features/auth/compat/sdk-types";
import type { IOspAuthModule } from "@/src/features/auth/compat/sdk-types";
import { getItem } from "utils";
import { favoritesCenter } from "@store";
import { LoginStore } from "@/src/store/modules/login";
import { UserStore } from "@/src/store/modules/user";
import { hasJustLoggedOutFlag } from "./authSessionSyncRefs";

async function syncSdkCredentialsFromOspCache(
  authModule: IOspAuthModule,
): Promise<void> {
  const cache = authCacheManager.getOspLoginCache();
  if (!cache?.accessToken) {
    return;
  }
  const stored = await authModule.storage.getCredentials();
  if (!stored?.accessToken) {
    await authModule.storage.setCredentials({
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken || "",
    });
  }
  if (cache.idToken) {
    try {
      await authModule.storage.setIdToken(cache.idToken);
    } catch (error) {
      log.debug("[syncAuthSessionFromSharedStorage] setIdToken skipped", error);
    }
  }
}

/**
 * 从共享 localStorage / OSP 缓存恢复业务登录态（多 WebView 接收 `auth-session-updated` 时调用）。
 */
export async function syncAuthSessionFromSharedStorage(
  authModule?: IOspAuthModule | null,
): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  if (hasJustLoggedOutFlag()) {
    log.debug(
      "[syncAuthSessionFromSharedStorage] skip restore: user just logged out",
    );
    return false;
  }

  const ospLoginCache = authCacheManager.getOspLoginCache();
  if (!ospLoginCache?.accessToken) {
    return false;
  }

  if (
    LoginStore.isLoggedIn &&
    LoginStore.auth?.accessToken === ospLoginCache.accessToken
  ) {
    log.debug(
      "[syncAuthSessionFromSharedStorage] already logged in with same token",
    );
    return true;
  }

  if (ospLoginCache.isRefreshAccessToken) {
    log.debug("[syncAuthSessionFromSharedStorage] token refresh in progress");
    return false;
  }

  if (authModule) {
    try {
      await syncSdkCredentialsFromOspCache(authModule);
    } catch (error) {
      log.error(
        "[syncAuthSessionFromSharedStorage] sync SDK credentials failed",
        error,
      );
      return false;
    }
  }

  const ownerInfoCache = authCacheManager.getOwnerInfoCache(
    getItem("_user_id") || undefined,
  );

  UserStore.accessToken = ospLoginCache.accessToken;
  if (ownerInfoCache?.ownerInfo) {
    UserStore.fetchUserInfoFromCache();
  }

  LoginStore.loginComplete({
    loginType: ospLoginCache.loginType ?? (undefined as unknown as LoginType),
    eoaAddress:
      ospLoginCache.loginInfo?.eoaAddress ||
      ownerInfoCache?.ownerInfo?.eoaAddress ||
      null,
    auth: {
      accessToken: ospLoginCache.accessToken,
      refreshToken: ospLoginCache.refreshToken || "",
      idToken: ospLoginCache.idToken ?? "",
    },
  });

  favoritesCenter.setCurrentUserId(
    UserStore.ownerInfo?.id || ownerInfoCache?.ownerInfo?.id || "",
  );

  if (!ownerInfoCache?.ownerInfo) {
    void UserStore.fetchUserInfo("", true);
  }

  log.info("[syncAuthSessionFromSharedStorage] session restored from cache", {
    loginType: ospLoginCache.loginType,
  });
  return true;
}
